import { useEffect, useState } from "react";
import { listWorkflows, deleteWorkflow } from "../../api/agentApi";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Toolbar,
  Alert,
  Divider,
  Chip,
} from "@mui/material";

interface Workflow {
  id: string;
  name: string;
  version: number;
  status: string;
  created_at: string;
}

export default function WorkflowListPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<string>("");

  useEffect(() => {
    listWorkflows()
      .then((data: Workflow[]) => setWorkflows(data || []))
      .catch((err: Error) => setError("Failed to load workflows"));
  }, []);

  const handleDelete = async (workflowId: string): Promise<void> => {
    if (
      !window.confirm(`Delete workflow '${workflowId}'? This cannot be undone.`)
    )
      return;
    setDeleting(workflowId);
    try {
      await deleteWorkflow(workflowId);
      setWorkflows(workflows.filter((w: Workflow) => w.id !== workflowId));
    } catch (e) {
      setError("Failed to delete workflow.");
    }
    setDeleting("");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toolbar
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ pl: 0 }}>
          Workflows
        </Typography>
        <Button
          component={Link}
          to="/workflow/generate"
          variant="contained"
          color="primary"
        >
          Create New Workflow
        </Button>
      </Toolbar>
      <Divider sx={{ mb: 2 }} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700 }}>Workflow ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows.map((workflow: Workflow) => (
                <TableRow key={workflow.id} hover sx={{ fontSize: "0.95rem" }}>
                    <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  <Link to={`/workflow/${workflow.id}`} style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                    {workflow.id}
                  </Link>
                </TableCell>
                <TableCell>{workflow.name}</TableCell>
                <TableCell>{workflow.version}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={workflow.status || "Unverified"}
                    sx={{
                      bgcolor:
                        workflow.status === "active" ? "#43a047" : "#e0e0e0",
                      color: "#fff",
                      fontWeight: 500,
                      borderRadius: "9999px", // fully rounded
                      height: 24,
                      px: 1.5,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {new Date(workflow.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleDelete(workflow.id)}
                    disabled={deleting === workflow.id}
                    color="error"
                    variant="outlined"
                    size="small"
                  >
                    {deleting === workflow.id ? "Deleting..." : "Delete"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {workflows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No workflows found. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
